'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { CheckCircleIcon, SpinnerIcon } from '@/components/icons';
import { cn } from '@/lib/utils';
import { useTheme } from 'next-themes';
import {
  useCheckoutStore,
  selectPaymentUrl,
  selectPaymentOrderId,
} from '@/stores/checkout-store';
import { useCartStore } from '@/stores/cart-store';

type PaymentStatus = 'loading' | 'paying' | 'success';

export function PaymentStep() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const paymentUrl = useCheckoutStore(selectPaymentUrl);
  const paymentOrderId = useCheckoutStore(selectPaymentOrderId);
  const clearPaymentData = useCheckoutStore((s) => s.clearPaymentData);
  const resetCheckout = useCheckoutStore((s) => s.resetCheckout);
  const closeCheckout = useCheckoutStore((s) => s.closeCheckout);
  const clearCart = useCartStore((s) => s.clearCart);

  const [status, setStatus] = useState<PaymentStatus>('loading');
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Poll payment status
  const checkPaymentStatus = useCallback(async () => {
    if (!paymentOrderId) return;
    try {
      const res = await fetch(`/api/orders/check-payment/${paymentOrderId}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.payment_status === 'paid') {
        setStatus('success');
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        // Delay then redirect
        setTimeout(() => {
          clearCart();
          clearPaymentData();
          resetCheckout();
          closeCheckout();
          window.location.href = `/success?order=${paymentOrderId}`;
        }, 2000);
      }
    } catch {
      // Silently retry
    }
  }, [paymentOrderId, clearCart, clearPaymentData, resetCheckout, closeCheckout]);

  // Start polling
  useEffect(() => {
    if (!paymentUrl || !paymentOrderId) return;
    setStatus('loading');
    setIframeLoaded(false);

    const startDelay = setTimeout(() => {
      pollingRef.current = setInterval(checkPaymentStatus, 4000);
    }, 5000);

    return () => {
      clearTimeout(startDelay);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [paymentUrl, paymentOrderId, checkPaymentStatus]);

  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
    if (status === 'loading') setStatus('paying');
  }, [status]);

  if (!paymentUrl) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <SpinnerIcon weight="bold" className="w-8 h-8 text-[var(--primary)] animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="absolute inset-0 overflow-y-auto overscroll-contain"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {/* Loading state */}
      {!iframeLoaded && status !== 'success' && (
        <div className={cn(
          'fixed inset-0 z-10 flex flex-col items-center justify-center gap-3',
          isDark ? 'bg-[var(--bg-primary)]' : 'bg-white'
        )}>
          <SpinnerIcon weight="bold" className="w-8 h-8 text-[var(--primary)] animate-spin" />
          <span className="text-sm text-[var(--text-secondary)]">
            Загрузка формы оплаты...
          </span>
        </div>
      )}

      {/* Success state */}
      {status === 'success' && (
        <div className={cn(
          'fixed inset-0 z-20 flex flex-col items-center justify-center gap-4',
          isDark ? 'bg-[var(--bg-primary)]' : 'bg-white'
        )}>
          <div className={cn(
            'w-20 h-20 rounded-pill flex items-center justify-center',
            'bg-[var(--success)]/10'
          )}>
            <CheckCircleIcon weight="fill" className="w-12 h-12 text-[var(--success)]" />
          </div>
          <div className="text-center px-6">
            <p className="text-lg font-semibold text-[var(--text-primary)]">
              Оплата прошла!
            </p>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              Перенаправляем на страницу заказа...
            </p>
          </div>
        </div>
      )}

      {/* PayKeeper iframe — large fixed height, parent scrolls */}
      <iframe
        src={paymentUrl}
        className={cn(
          'w-full border-0',
          (!iframeLoaded || status === 'success') && 'invisible'
        )}
        style={{ height: '1400px' }}
        onLoad={handleIframeLoad}
        allow="payment"
        sandbox="allow-scripts allow-forms allow-same-origin allow-top-navigation allow-top-navigation-by-user-activation allow-popups allow-popups-to-escape-sandbox"
      />
    </div>
  );
}
